/* eslint-disable react/no-array-index-key */
import React from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { Tooltip } from '@roo/roo'
import { IFundOverview } from '../../utils/types'
import { CooperateModeEnum } from '@/common/utils/consts'
import { replaceMoneySymbol, getI18nTime } from '@/common/utils/i18nFuncs'
import { getDateTimePattern } from '@/common/utils/i18n'
import styles from './index.module.less'

interface IFundOverviewProps {
  data?: IFundOverview
  loading?: boolean
  onViewBill?: () => void // 查看账单明细回调
}

const FundOverview: React.FC<IFundOverviewProps> = ({ data, loading, onViewBill }) => {
  const { t } = useTranslation()

  const displayData = data
  const showSkeleton = loading && !displayData

  if (showSkeleton) {
    return (
      <div className={styles.overview}>
        <div className={styles.header}>
          <div className={styles.title}>{t('Keeta_EE_Overview_9PUz', '总览')}</div>
          <div className={`${styles.skeletonBlock} ${styles.updateTimeSkeleton}`} />
        </div>
        <div className={`${styles.content} ${styles.contentPadding}`}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={styles.item}>
              <div className={`${styles.skeletonBlock} ${styles.labelSkeleton}`} />
              <div className={`${styles.skeletonBlock} ${styles.valueSkeleton}`} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // 判断是预充值还是后结算场景（授信场景）
  const isPrepaid = displayData?.cooperateMode === CooperateModeEnum.PRE_CHARGE
  const items = isPrepaid
    ? [
      // 预充值场景：只显示可用额度
      {
        label: t('Keeta_EE_Available_line_of_credit_NqE9', '可用额度'),
        value: replaceMoneySymbol(displayData?.accountBalance?.displayText || '--'),
        highlight: true,
        tooltip: null,
      },
    ]
    : [
      // 授信场景：显示全部四个字段
      {
        label: t('Keeta_EE_Available_line_of_credit_NqE9', '可用额度'),
        value: replaceMoneySymbol(displayData?.accountBalance?.displayText || '--'),
        highlight: true,
        tooltip: null,
      },
      {
        label: t('Keeta_EE_Enterprise_line_of_credit_RUQN', '企业授信额度'),
        value: replaceMoneySymbol(displayData?.creditLimit?.displayText || '--'),
        tooltip: t('Keeta_EE_keeta_amount_XQee', 'Keeta根据企业资质及在Keeta预充资金综合获批的授信金额，即Keeta可垫付的最大金额，可通过给Keeta充值进行额度提升。'),
      },
      {
        label: t('Keeta_EE_Output_amount_pxJr', '未出账金额'),
        value: replaceMoneySymbol(displayData?.unbilledAmount?.displayText || '--'),
        tooltip: t('Keeta_EE_orders_transactions_9Q3h', '当前还未生成账单的员工交易订单金额（已剔除交易订单中的退款金额）。'),
      },
      {
        label: t('Keeta_EE_Payment_billing_Q2Or', '已出账未支付金额	'),
        value: replaceMoneySymbol(displayData?.settlementStatementBalance?.displayText || '--'),
        tooltip: t('Keeta_EE_Amount_Current_vY2t', '当前已生成账单但还未打款支付的金额（已剔除交易订单中的退款金额）。'),
        showBillLink: true, // 显示账单明细链接
      },
    ]

  return (
    <div className={styles.overview}>
      <div className={styles.header}>
        <div className={styles.title}>
          {t('Keeta_EE_Overview_9PUz', '总览')}
        </div>
        {displayData?.updateEndTime && (
          <div className={styles.updateTime}>
            {t('Keeta_EE_updates_data_9UHX', '数据更新至')}{getI18nTime(displayData.updateEndTime, getDateTimePattern())}
          </div>
        )}
      </div>
      <div className={`${styles.content} ${styles.contentPadding}`}>
        {items.map((item, index) => (
          <div key={index} className={styles.item}>
            <div>
              <div className={styles.label}>
                {item.tooltip ? (
                  <Tooltip content={item.tooltip} placement="top">
                    <span className={styles.labelWithTip}>
                      {item.label}
                      <span className={styles.tipIcon}>?</span>
                    </span>
                  </Tooltip>
                ) : (
                  item.label
                )}
              </div>
              {index === 0 && displayData?.accountBalanceAlert && (
                <div className={styles.desc}>{t('Keeta_EE_available_as_soon_as_possible_YGWb', '可用额度不足，请尽快打款')}</div>
              )}
            </div>
            <div
              className={`${styles.value} ${item.highlight ? styles.highlight : ''} ${index === items.length - 1 ? styles.lastItem : ''}`}
              style={{ marginTop: index !== 0 && displayData?.accountBalanceAlert ? '22px' : '0' }}
            >
              {item.value}
              {(item as any).showBillLink && onViewBill && (
                <div
                  className={styles.billLink}
                  onClick={onViewBill}
                >
                  {t('Keeta_EE_Details_bills_hL2q', '账单明细')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default FundOverview
